import { create } from "zustand";

export type Permission = {
  id: string;
  key: string;
  impliesPermissionIds: string[];
};

export type PermissionNode = {
  id: string;
  label: string;
  disabled: boolean;
  checked: boolean;
  tooltip: boolean;
  wasForced?: boolean;
};

type PermissionsState = {
  permissionsList: Permission[];
  nodes: Record<string, PermissionNode>;
  setPermissionsList: (
    permissions: Permission[],
    initiallyChecked?: string[],
  ) => void;
  togglePermission: (checkedIds: string[]) => void;
  getSelectedIds: () => string[];
};

function getAllParentIds(impliedId: string, permissionsList: Permission[]) {
  return permissionsList
    .filter((p) => p.impliesPermissionIds.includes(impliedId))
    .map((p) => p.id);
}

function recomputeNodesStates(
  nodes: Record<string, PermissionNode>,
  permissionsList: Permission[],
) {
  const newNodes: Record<string, PermissionNode> = { ...nodes };

  for (const node of Object.values(newNodes)) {
    const parents = getAllParentIds(node.id, permissionsList);
    const hasParentChecked = parents.some((pid) => newNodes[pid]?.checked);

    if (hasParentChecked) {
      node.checked = true;
      node.disabled = true;
      node.tooltip = true;
      node.wasForced = true;
    } else {
      node.disabled = false;
      node.tooltip = false;
      if (node.wasForced) {
        node.checked = false;
        node.wasForced = false;
      }
    }
  }
  return newNodes;
}

export const usePermissionsStore = create<PermissionsState>((set, get) => ({
  permissionsList: [],
  nodes: {},
  setPermissionsList: (permissions, initiallyChecked = []) => {
    const nodes: Record<string, PermissionNode> = {};
    for (const p of permissions) {
      const isInitiallyChecked = initiallyChecked.includes(p.id);
      nodes[p.id] = {
        id: p.id,
        label: p.key,
        disabled: isInitiallyChecked,
        checked: isInitiallyChecked,
        tooltip: isInitiallyChecked,
      };
    }
    const recalculated = recomputeNodesStates(nodes, permissions);
    set({ permissionsList: permissions, nodes: recalculated });
  },
  togglePermission: (checkedIds: string[]) => {
    const { permissionsList, nodes } = get();
    const newNodes: Record<string, PermissionNode> = { ...nodes };
    for (const node of Object.values(newNodes)) {
      if (!node.disabled) {
        node.checked = checkedIds.includes(node.id);
      }
    }
    const recalculated = recomputeNodesStates(newNodes, permissionsList);
    set({ nodes: recalculated });
  },
  getSelectedIds: () => {
    const { nodes } = get();
    return Object.values(nodes)
      .filter((node) => node.checked)
      .map((node) => node.id);
  },
}));
